// ── Style tokens ────────────────────────────────────────────────────────────
const S = {
  wrapper: {
    marginTop: '10px', padding: '40px', border: '3px solid #1a5c2a',
    fontFamily: "'Georgia', serif", color: '#0a1f0e', lineHeight: '1.7',
    background: '#f2faf4',
  },
  h1: {
    fontSize: '2rem', fontWeight: '700', borderBottom: '3px solid #2e8b47',
    paddingBottom: '8px', marginBottom: '20px', marginTop: '32px', color: '#145a24',
  },
  subtitle: {
    fontSize: '0.95rem', color: '#3a7a4a', marginTop: '16px', marginBottom: '28px', fontStyle: 'italic',
  },
  h2: {
    fontSize: '1.3rem', fontWeight: '700', color: '#1e7a36', marginTop: '36px',
    marginBottom: '12px', borderLeft: '4px solid #2e8b47', paddingLeft: '10px',
  },
  h3: {
    fontSize: '1.05rem', fontWeight: '700', color: '#2a6a3a', marginTop: '20px',
    marginBottom: '8px',
  },
  p: { marginBottom: '12px', fontSize: '0.97rem' },
  infoBox: {
    background: '#e8f5ec', border: '2px solid #2e8b47', borderRadius: '6px',
    padding: '16px 20px', margin: '16px 0',
  },
  infoBoxTitle: {
    fontWeight: '700', fontFamily: "'Courier New', monospace", fontSize: '0.88rem',
    color: '#145a24', marginBottom: '8px',
  },
  infoBoxItem: {
    fontSize: '0.9rem', margin: '4px 0', paddingLeft: '12px',
    borderLeft: '2px solid #2e8b47',
  },
  legendBox: {
    display: 'flex', flexWrap: 'wrap', gap: '12px', margin: '16px 0 20px 0',
    background: '#dff0e4', border: '1px solid #7abf8a', borderRadius: '6px',
    padding: '12px 16px',
  },
  legendItem: { display: 'flex', alignItems: 'center', gap: '6px', fontSize: '0.85rem' },
  legendDot: { width: '14px', height: '14px', borderRadius: '3px', flexShrink: 0 },
  noteBox: {
    background: '#fff9e6', border: '1px solid #c8a800', borderRadius: '5px',
    padding: '10px 14px', margin: '12px 0', fontSize: '0.88rem', color: '#5a4500',
  },
  dangerBox: {
    background: '#fde8e8', border: '1px solid #c83030', borderRadius: '5px',
    padding: '10px 14px', margin: '12px 0', fontSize: '0.88rem', color: '#5a0000',
  },
};

// ── Rating colours ───────────────────────────────────────────────────────────
const RATING_COLORS = {
  'Excellent':    { bg: '#c6efce', text: '#1a5c1a', border: '#4caf50' },
  'Good':         { bg: '#d4edda', text: '#1c5a2a', border: '#6abf7a' },
  'Moderate':     { bg: '#fff3cd', text: '#6a4a00', border: '#ffc107' },
  'Limited':      { bg: '#fde2b8', text: '#7a3500', border: '#ff9800' },
  'Poor':         { bg: '#f8d7da', text: '#7a0010', border: '#f44336' },
  'Yes':          { bg: '#c6efce', text: '#1a5c1a', border: '#4caf50' },
  'No':           { bg: '#f8d7da', text: '#7a0010', border: '#f44336' },
  'Partial':      { bg: '#fff3cd', text: '#6a4a00', border: '#ffc107' },
  'High':         { bg: '#f8d7da', text: '#7a0010', border: '#f44336' },
  'Medium':       { bg: '#fff3cd', text: '#6a4a00', border: '#ffc107' },
  'Low':          { bg: '#c6efce', text: '#1a5c1a', border: '#4caf50' },
  'Permanent':    { bg: '#f8d7da', text: '#7a0010', border: '#f44336' },
  'Removable':    { bg: '#c6efce', text: '#1a5c1a', border: '#4caf50' },
  'Wicking':      { bg: '#fde2b8', text: '#7a3500', border: '#ff9800' },
  'N/A':          { bg: '#e9ecef', text: '#555',    border: '#aaa' },
};

function RatingBadge({ value }) {
  const key = Object.keys(RATING_COLORS).find(k => value && value.startsWith(k));
  const style = key ? RATING_COLORS[key] : { bg: '#e9ecef', text: '#444', border: '#bbb' };
  return (
    <span style={{
      background: style.bg, color: style.text, border: `1px solid ${style.border}`,
      borderRadius: '4px', padding: '2px 7px', fontSize: '0.82rem', fontWeight: '600',
      whiteSpace: 'nowrap', display: 'inline-block',
    }}>{value}</span>
  );
}

function Table2({ rows, col1Label = 'Property', col2Label = 'Detail' }) {
  const thStyle = {
    background: '#1e7a36', color: '#fff', padding: '10px 14px',
    textAlign: 'left', fontWeight: '700', fontSize: '0.9rem',
  };
  const tdBase = {
    padding: '10px 14px', borderBottom: '1px solid #b8dfc2', verticalAlign: 'top',
    fontSize: '0.9rem', fontFamily: "'Courier New', monospace", whiteSpace: 'pre-wrap',
  };
  return (
    <table style={{ width: '100%', borderCollapse: 'collapse', marginBottom: '20px' }}>
      <thead><tr>
        <th style={{ ...thStyle, width: '240px' }}>{col1Label}</th>
        <th style={thStyle}>{col2Label}</th>
      </tr></thead>
      <tbody>
        {rows.map(([label, content], i) => (
          <tr key={i}>
            <td style={{ ...tdBase, background: i % 2 === 0 ? '#e8f5ec' : '#f5faf7', fontWeight: '600', color: '#145a24', fontFamily: "'Georgia', serif" }}>{label}</td>
            <td style={{ ...tdBase, background: i % 2 === 0 ? '#e8f5ec' : '#f5faf7' }}>{content}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

function InfoBox({ title, items }) {
  return (
    <div style={S.infoBox}>
      <div style={S.infoBoxTitle}>{title}</div>
      {items.map((item, i) => <div key={i} style={S.infoBoxItem}>{item}</div>)}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// DATA
// ─────────────────────────────────────────────────────────────────────────────

// ── Loctite threadlocker data ────────────────────────────────────────────────
const THREADLOCKERS = [
  {
    grade: '222 (Purple)', strength: 'Low', removable: 'Yes', tempMax: '150 °C',
    breakTorque: '2–5 Nm (M6)', fixture: '10 min', fullCure: '24 h',
    gapFill: 'Up to 0.15 mm', viscosity: '~650 cP',
    bestFor: 'Small screws M2–M8, instruments, optics, adjustable assemblies',
    notFor: 'Structural fasteners; cannot substitute higher grades on safety bolts',
    note: 'Low-strength intentional — easy hand-tool disassembly; prevents vibration loosening on fine threads',
  },
  {
    grade: '243 (Blue)', strength: 'Medium', removable: 'Yes', tempMax: '150 °C',
    breakTorque: '10–25 Nm (M10)', fixture: '15 min', fullCure: '24 h',
    gapFill: 'Up to 0.15 mm', viscosity: '~600 cP',
    bestFor: 'M6–M20 general fasteners, motors, flanges, machinery',
    notFor: 'Passive metals without primer (stainless, zinc); plastics without Loctite SF 770 primer',
    note: 'Most widely used grade; hand-tool removable; oil-tolerant formulation for contaminated fasteners',
  },
  {
    grade: '270 (Green — wicking)', strength: 'High', removable: 'Partial', tempMax: '150 °C',
    breakTorque: '35–45 Nm (M10)', fixture: '60 min', fullCure: '24 h',
    gapFill: 'Up to 0.10 mm', viscosity: '~200 cP (low)',
    bestFor: 'Already-assembled fasteners; blind-hole retrofitting; wicking into pre-assembled joints',
    notFor: 'Open assembly where controlled application is needed; not for gap >0.1 mm',
    note: 'Low viscosity deliberately — applied to tightened bolt and wicks into thread by capillary action',
  },
  {
    grade: '263 (Black)', strength: 'High', removable: 'Partial', tempMax: '150 °C',
    breakTorque: '35–50 Nm (M10)', fixture: '20 min', fullCure: '24 h',
    gapFill: 'Up to 0.15 mm', viscosity: '~900 cP',
    bestFor: 'High-vibration machinery; automotive drivetrain fasteners; black colour for cosmetic match',
    notFor: 'Not substitutable for 271/272 in critical structural joints; requires heat or press for removal',
    note: 'Coloured black to distinguish from other grades; heat gun to ~150 °C breaks cure for disassembly',
  },
  {
    grade: '271 (Red)', strength: 'High / Permanent', removable: 'No (heat req.)', tempMax: '150 °C',
    breakTorque: '35–60 Nm (M10)', fixture: '30 min', fullCure: '24 h',
    gapFill: 'Up to 0.15 mm', viscosity: '~1,500 cP',
    bestFor: 'M10–M25 studs, press-fit retainers, permanent fixtures, exhaust studs, structural bolts',
    notFor: 'Any fastener requiring future routine disassembly; do not use on fine thread small screws',
    note: 'Apply heat ≥230 °C then break torque while hot; once cooled re-cures. Use with primer on passive metals',
  },
  {
    grade: '272 (Red — high temp)', strength: 'High / Permanent', removable: 'No (heat req.)', tempMax: '230 °C',
    breakTorque: '40–65 Nm (M10)', fixture: '45 min', fullCure: '24 h',
    gapFill: 'Up to 0.15 mm', viscosity: '~2,000 cP',
    bestFor: 'Exhaust manifold studs, engine mounts, high-heat applications to 230 °C continuous',
    notFor: 'Standard applications where 271 suffices; higher cost not justified at <150 °C',
    note: 'Rated for 230 °C continuous; short excursions to 235 °C tolerated; requires SF 7649 primer on SS',
  },
  {
    grade: '290 (Green — wicking high str)', strength: 'High', removable: 'No (heat req.)', tempMax: '150 °C',
    breakTorque: '40–55 Nm (M10)', fixture: '30–60 min', fullCure: '24 h',
    gapFill: 'Up to 0.08 mm', viscosity: '~7 cP (very low)',
    bestFor: 'Wicking into fine pre-assembled threaded joints; locking set screws after adjustment; sealing fine porosity',
    notFor: 'Blind holes where excess cannot escape; coarse threads with large gaps',
    note: 'Lowest viscosity threadlocker; extremely fast capillary penetration; also used for impregnating sintered metal parts',
  },
];

// ── Retaining compounds ──────────────────────────────────────────────────────
const RETAINING = [
  {
    grade: '603 (Green)', type: 'Retaining', strength: 'High', tempMax: '150 °C',
    shearStr: '17 MPa (steel)', fixture: '10 min', fullCure: '24 h',
    gapFill: '0.10 mm', viscosity: '~400 cP',
    bestFor: 'Bearings, bushings, slip-fit cylindrical assemblies; slip fits where pressing is impractical',
    notFor: 'Interference fits where press alone is sufficient and no thermal cycling; large gaps >0.15 mm',
    note: 'Designed for slip/transition fit parts; cures in thin film. Do NOT apply and then force-press — apply and slide together',
  },
  {
    grade: '609 (Green)', type: 'Retaining', strength: 'Medium', tempMax: '150 °C',
    shearStr: '11 MPa (steel)', fixture: '20 min', fullCure: '24 h',
    gapFill: '0.25 mm', viscosity: '~250 cP',
    bestFor: 'Larger gap retaining — worn bore repairs, adjustable assemblies, rotor/stator retention',
    notFor: 'High-precision fits; structural loads without mechanical support',
    note: 'Higher gap capability than 603; useful for repair applications where bore has worn beyond press-fit tolerance',
  },
  {
    grade: '638 (Green)', type: 'Retaining', strength: 'Very High', tempMax: '150 °C',
    shearStr: '25 MPa (steel)', fixture: '5 min', fullCure: '24 h',
    gapFill: '0.15 mm', viscosity: '~500 cP',
    bestFor: 'Maximum-strength retention; gear/shaft assemblies, dowel pins, heavy-load bearings',
    notFor: 'Any assembly intended to be disassembled — effectively permanent on steel',
    note: 'Fastest fixture of retaining range (5 min); highest shear strength; disassembly requires press and heat',
  },
  {
    grade: '648 (Green — high temp)', type: 'Retaining', strength: 'High', tempMax: '200 °C',
    shearStr: '25 MPa (steel)', fixture: '10 min', fullCure: '24 h',
    gapFill: '0.15 mm', viscosity: '~600 cP',
    bestFor: 'Hot applications: turbine housings, motor end bells, pump impellers, exhaust-side assemblies',
    notFor: 'Standard temperature use where 638 suffices (higher cost)',
    note: 'Elevated temperature grade with identical strength to 638; required where continuous service >150 °C',
  },
];

// ── Sealants & pipe compounds ─────────────────────────────────────────────────
const SEALANTS = [
  {
    grade: '545 (Brown)', type: 'Hydraulic/Pneumatic', temp: '−55 to +150 °C',
    pressure: '690 bar (hydraulic)', fixture: '30 min', fullCure: '72 h',
    bestFor: 'Hydraulic fittings, pneumatic connectors, instrumentation ports, NPT threads',
    note: 'Low viscosity; designed for metal hydraulic and pneumatic fittings; resists fuels, oils, hydraulic fluid',
  },
  {
    grade: '567 (White)', type: 'Pipe Sealant', temp: '−55 to +150 °C',
    pressure: '200 bar (water/gas)', fixture: '60 min', fullCure: '24 h',
    bestFor: 'Stainless and non-ferrous pipes, water/gas plumbing, food-grade fittings (NSF listed)',
    note: 'White thread sealant; suitable for plastic pipes without damaging them; replaces PTFE tape on metal threads',
  },
  {
    grade: '569 (Grey)', type: 'Pipe Sealant', temp: '−55 to +150 °C',
    pressure: '690 bar (hydraulic/gas)', fixture: '60 min', fullCure: '24 h',
    bestFor: 'High-pressure hydraulic, gas, and oil-line fittings; industrial piping systems',
    note: 'Thixotropic paste; no dripping; resistant to LP/HP gas, water, oils; cures on active/inactive metals',
  },
  {
    grade: '572 (White)', type: 'Pipe Sealant', temp: '−55 to +150 °C',
    pressure: '690 bar', fixture: '60 min', fullCure: '24 h',
    bestFor: 'General-purpose pipe thread sealing; fuel, oil, gas, water; replaces PTFE tape',
    note: 'Most widely used pipe sealant grade; disassembly without pipe wrench damage; re-usable threads',
  },
  {
    grade: '5900 (RTV Flange)', type: 'Gasket / RTV', temp: '−55 to +230 °C',
    pressure: 'N/A (gasket)', fixture: '20 min skin', fullCure: '24 h',
    bestFor: 'Formed-in-place gaskets, oil pans, gearbox covers, flanges without rigid gaskets',
    note: 'Replaces cut gaskets; black RTV silicone; oil/fuel resistant; apply bead, assemble within skin time, cure 24 h before oil fill',
  },
  {
    grade: '518 (Blue — anaerobic flange)', type: 'Gasket / Anaerobic', temp: '−55 to +150 °C',
    pressure: '200 bar', fixture: '30 min', fullCure: '24 h',
    bestFor: 'Close-tolerance flanges with machined surfaces, inspection covers, transmission housings',
    note: 'Not for rubber or soft metal flanges; excellent for cast iron, steel; resists oils and coolants; disassemblable with hand tools',
  },
];

// ── Adhesives data ────────────────────────────────────────────────────────────
const ADHESIVES = [
  {
    name: 'Loctite 401 / 406 / 408 (Cyanoacrylate — CA / Super Glue)',
    category: 'Cyanoacrylate', curingMech: 'Moisture (surface & air humidity)',
    fixture: '10–60 s', fullCure: '24 h', gapFill: 'Up to 0.15 mm (thin)',
    shearStr: '15–25 MPa (steel)', tempRange: '−55 to +120 °C',
    metalBond: 'Excellent', plasticBond: 'Good', rubberBond: 'Good', woodBond: 'Good',
    waterResist: 'Moderate', solventResist: 'Limited', heatResist: 'Limited',
    bestFor: 'Fast repair bonds, metal-rubber, rubber-to-metal gaskets, fixture during cure of secondary adhesive, small part assembly',
    notFor: 'Gap filling >0.15 mm; flexible joints; high-temp; long-term outdoor exposure; acidic surfaces',
    notes: '401 = general; 406 = plastics/rubber optimised; 408 = gap-filling viscosity (~400 cP); 770 primer required for polyolefins. Bonds fingers instantly — have acetone ready.',
  },
  {
    name: 'Loctite Hysol EA 9394 / 3M DP460 (Structural Epoxy 2-part)',
    category: 'Epoxy (2K)', curingMech: 'Chemical — mix hardener + resin',
    fixture: '2–4 h (23 °C)', fullCure: '24–72 h (or 1 h at 80 °C)',
    gapFill: 'Up to 5 mm', shearStr: '30–45 MPa (Al)', tempRange: '−55 to +120 °C',
    metalBond: 'Excellent', plasticBond: 'Good', rubberBond: 'Limited', woodBond: 'Excellent',
    waterResist: 'Excellent', solventResist: 'Excellent', heatResist: 'Good',
    bestFor: 'Structural joints — metal-to-metal, composites, bonding dissimilar materials; fills gaps; load-bearing',
    notFor: 'Flexible substrates (brittle); rubber; very fast cycles; substrates with oil contamination',
    notes: 'Mix ratio critical — most fail from incorrect ratio not substrate prep; surface abrasion + solvent wipe essential; heat curing doubles strength. Pot life: ~30–90 min depending on grade.',
  },
  {
    name: 'Araldite Standard / Rapid (Consumer 2K Epoxy)',
    category: 'Epoxy (2K)', curingMech: 'Chemical — mix hardener + resin',
    fixture: '60 min (rapid) / 6 h (standard)', fullCure: '24 h',
    gapFill: 'Up to 3 mm', shearStr: '12–18 MPa', tempRange: '−40 to +100 °C',
    metalBond: 'Excellent', plasticBond: 'Moderate', rubberBond: 'Limited', woodBond: 'Excellent',
    waterResist: 'Good', solventResist: 'Good', heatResist: 'Moderate',
    bestFor: 'Workshop repairs, bonding metal/wood/glass, hobby and general-purpose structural bonds',
    notFor: 'Continuous immersion; high-temp service; flexible joints; contaminated surfaces',
    notes: 'Mix 1:1 by volume; rapid grade trades strength for speed; standard grade preferred for maximum strength. Sold in twin-syringe or tube packs.',
  },
  {
    name: 'Loctite EA 3430 / Permabond ET500 (Methacrylate / Acrylic 2K)',
    category: 'Acrylic (2K MMA)', curingMech: 'Chemical — activator or 2K mix',
    fixture: '3–5 min (fast grade)', fullCure: '30–60 min',
    gapFill: 'Up to 5 mm', shearStr: '25–35 MPa', tempRange: '−40 to +120 °C',
    metalBond: 'Excellent', plasticBond: 'Excellent', rubberBond: 'Good', woodBond: 'Good',
    waterResist: 'Excellent', solventResist: 'Good', heatResist: 'Good',
    bestFor: 'Metal, composites, plastics; outdoor structures; joints requiring peel and impact resistance; bonding PE/PP with activator',
    notFor: 'Silicone; PTFE; very high temp (>150 °C); solvent-sensitive substrates',
    notes: 'Superior peel/impact vs epoxy; strong acrylic odour — ventilate well; activator-based versions allow open-time flexibility; excellent for fiberglass and CFRP.',
  },
  {
    name: 'Sikaflex-252 / Loctite PL 500 (Polyurethane — PU)',
    category: 'Polyurethane (1K)', curingMech: 'Moisture (atmospheric)',
    fixture: '30–60 min skin', fullCure: '3–7 days',
    gapFill: 'Up to 20 mm', shearStr: '4–8 MPa', tempRange: '−40 to +90 °C',
    metalBond: 'Good', plasticBond: 'Good', rubberBond: 'Excellent', woodBond: 'Excellent',
    waterResist: 'Excellent', solventResist: 'Moderate', heatResist: 'Limited',
    bestFor: 'Flexible bonds, panel bonding, wood/concrete, vibration-damping joints, sealing + bonding combination',
    notFor: 'Rigid structural joints (too flexible); high-temp service; silicone substrates',
    notes: 'Remains flexible when cured — excellent for dissimilar materials with different CTE; paintable; apply bead, assemble, clamp lightly. Full cure slow — 3–7 days for max strength.',
  },
  {
    name: 'Sikasil / Dow Corning 732 / GE RTV (Silicone — RTV)',
    category: 'Silicone RTV (1K)', curingMech: 'Moisture (atmospheric)',
    fixture: '30–60 min skin', fullCure: '24–72 h',
    gapFill: 'Up to 25 mm', shearStr: '1–3 MPa', tempRange: '−60 to +200 °C',
    metalBond: 'Moderate', plasticBond: 'Moderate', rubberBond: 'Excellent', woodBond: 'Limited',
    waterResist: 'Excellent', solventResist: 'Limited', heatResist: 'Excellent',
    bestFor: 'High-temp sealing, electrical enclosures, flexible gasketing, vibration isolation, glass bonding',
    notFor: 'Structural loads; paintable surfaces (silicone bleeds, prevents paint adhesion); bonding to itself (use SS primer)',
    notes: 'Acetic-acid cure (vinegar smell) — do not use on copper/brass (corrosive); neutral-cure grades available. Not paintable — plan for this in design.',
  },
  {
    name: 'Lord Fusor / SEM Panel Bond (Flexible Structural Adhesive)',
    category: 'Modified Epoxy / Urethane', curingMech: 'Chemical — 2K mix',
    fixture: '30–60 min', fullCure: '24 h',
    gapFill: 'Up to 6 mm', shearStr: '12–20 MPa', tempRange: '−40 to +120 °C',
    metalBond: 'Excellent', plasticBond: 'Excellent', rubberBond: 'Good', woodBond: 'Good',
    waterResist: 'Excellent', solventResist: 'Good', heatResist: 'Moderate',
    bestFor: 'Automotive body panel bonding, SMC/fiberglass repair, structural bonding with vibration/peel loading',
    notFor: 'Primary structural joints under high static load; oily/contaminated surfaces',
    notes: 'Automotive OEM specification adhesive; paintable; flexible enough to withstand panel flex and body twisting; weld-bond capable (adhesive + spot welds).',
  },
  {
    name: 'Loctite 480 / 495 (Toughened / Instant CA)',
    category: 'Toughened Cyanoacrylate', curingMech: 'Moisture',
    fixture: '10–30 s', fullCure: '24 h',
    gapFill: 'Up to 0.15 mm', shearStr: '18–22 MPa', tempRange: '−55 to +120 °C',
    metalBond: 'Excellent', plasticBond: 'Excellent', rubberBond: 'Excellent', woodBond: 'Good',
    waterResist: 'Good', solventResist: 'Limited', heatResist: 'Limited',
    bestFor: 'Rubber-to-metal, flexible substrates, impact-resistant instant bonds, shoe sole repair, gasket bonding',
    notFor: 'Gap filling; long-term immersion; high-temp applications',
    notes: '480 = rubber-bonding black toughened CA; 495 = clear general-purpose toughened CA. Both offer improved impact and peel resistance vs standard CA grades.',
  },
];

// ── General workshop consumables ─────────────────────────────────────────────
const CONSUMABLES = [
  {
    cat: 'Cutting Fluids', items: [
    { name: 'Neat Cutting Oil (Mineral)',       use: 'Heavy turning/milling (steel, stainless)', note: 'Maximum lubrication; fire risk if near flame; mist extraction critical; not water-miscible' },
    { name: 'Water-Soluble Coolant (Emulsion)', use: 'General CNC, drill/mill/turn',             note: 'Mix 5–10% with water; pH 8.5–9.5; monitor concentration (refractometer) and pH weekly; Brix test' },
    { name: 'Cutting Paste (Tapping compound)', use: 'Hand tapping, thread cutting, pipe dies',  note: 'High-viscosity paste; stays on tool; excellent for stainless and titanium tapping; sulphurised grades for hardened steel' },
    { name: 'WD-40 / Light Penetrating Oil',    use: 'Drilling aluminium, emergency cutting fluid', note: 'NOT a cutting fluid substitute; thin lubrication only; evaporates; use proper cutting oil for tool life' },
    { name: 'Aluminium-Specific Cutting Fluid', use: 'CNC aluminium, high-speed milling',        note: 'Prevents BUE (built-up edge); many mineral oils cause staining — use correct Al grade; often water-miscible' },
  ]},
  {
    cat: 'Thread-Forming & Assembly Compounds', items: [
    { name: 'Anti-Seize (Copper)',    use: 'Stainless fasteners, exhaust studs, high-temp bolts', note: 'Prevents galling on SS-to-SS; copper: to 980 °C; note torque reduction ~20% on coated threads vs dry spec' },
    { name: 'Anti-Seize (Nickel)',    use: 'High-temp alloy bolts, nuclear/food/marine environments', note: 'Nickel-free or nickel-based; higher temp rating than copper (>1000 °C); food-grade versions available' },
    { name: 'Anti-Seize (Zinc/Moly)', use: 'General fasteners, studs, spark plugs',               note: 'Lower temperature than copper; good general-purpose grade for automotive and industrial' },
    { name: 'PTFE Tape',              use: 'Pipe thread sealing (NPT, BSP)',                        note: 'Apply 2–3 wraps clockwise on male thread; do not overtighten — may crack fittings; not for flared or compression fittings' },
    { name: 'Thread Repair Insert (Heli-Coil)', use: 'Damaged thread repair',                     note: 'Drill → tap to insert size → install wire insert; restores full-strength thread in aluminium, cast iron, plastic' },
  ]},
  {
    cat: 'Lubricants', items: [
    { name: 'Grease (NLGI 2 — Lithium)',        use: 'General bearings, slideways, chassis',       note: 'Most common workshop grease; service to ~120 °C; incompatible with calcium/sodium base greases — purge bearing before switching type' },
    { name: 'Grease (High-Temp — Moly/Polyurea)', use: 'Wheel bearings, high-speed spindles',      note: 'Polyurea (electric motor bearings); moly for slow/high-load; check base oil viscosity for speed rating' },
    { name: 'Dry Lubricant (PTFE / Moly Spray)',  use: 'Slides, guides, threaded rods, hinges',    note: 'No oil residue; does not collect swarf; use on precision leadscrew and linear bearings; reapply every 4–8 weeks' },
    { name: 'Chain Lubricant',                    use: 'Roller chains, conveyor drives',            note: 'Light: high-speed chains; heavy: slow/high-load chains; apply on inner plate while chain runs slowly' },
    { name: 'Penetrating Oil (PB Blaster / Plus Gas)', use: 'Seized fasteners, rust removal',      note: 'Allow 15–60 min soak; heat + penetrant most effective; Kroil/PlusGas widely regarded as best penetrants; avoid CA-glued joints' },
    { name: 'Hydraulic Oil (ISO 46)',              use: 'Hydraulic jacks, presses, cylinders',      note: 'Match ISO viscosity grade to system specification; do not mix mineral and synthetic; change at contamination (milky = water ingress)' },
  ]},
  {
    cat: 'Abrasives', items: [
    { name: 'Sandpaper (Aluminium Oxide)',          use: 'Metal/wood hand sanding, prep',          note: 'Grits: 80 (aggressive strip), 120 (rough prep), 180–240 (pre-finish), 320–400 (fine prep for adhesive/paint)' },
    { name: 'Sandpaper (Silicon Carbide — wet/dry)', use: 'Metal finishing, primer flatting',      note: 'Harder grit; use wet for swarf clearance; 600–2000 used for paint cutting and polishing' },
    { name: 'Abrasive Flap Disc (40–120 grit)',     use: 'Angle grinder blending, weld dressing',  note: 'Zirconia for steel; ceramic for stainless/hard alloys; depressed-centre for aggressive removal; flat for blending' },
    { name: 'Scotch-Brite / Non-Woven Pad',         use: 'Surface prep for adhesive bonding, deburring', note: 'Critical for adhesive prep — scuff + solvent wipe; colour grades: green (medium), maroon (fine), grey (very fine)' },
    { name: 'Wire Wheel / Cup Brush (Bench Grinder)', use: 'Rust removal, scale, deburring',      note: 'Carbon steel for steel work; stainless wire for SS (prevent carbon contamination); face shield mandatory; RPM rating critical' },
    { name: 'Grinding Disc (Type 27 / 29)',          use: 'Weld clean-up, heavy stock removal',     note: 'Check RPM rating on disc vs grinder; never use cut-off disc for grinding or vice versa; inspect for cracks before each use' },
  ]},
  {
    cat: 'Welding Consumables', items: [
    { name: 'MIG Wire (ER70S-6 / 0.8–1.0 mm)',    use: 'General mild steel MIG welding',          note: 'ER70S-6: high deoxidiser, tolerates mill scale; 0.8 mm for <3 mm metal; 1.0 mm for thicker; store dry — moisture causes porosity' },
    { name: 'MIG Wire (ER308L — Stainless)',       use: 'MIG welding 304/316 SS',                   note: '308L for 304 SS; 316L wire for 316 SS; use tri-mix gas (He/Ar/CO₂) or Ar/CO₂ 98/2; low-carbon grade to prevent sensitisation' },
    { name: 'TIG Rod (ER70S-2 / ER308L)',          use: 'TIG welding mild steel and SS',            note: 'Keep rods dry and uncontaminated; triple-deoxidised ER70S-2 for root passes; cut off contaminated tip rather than risk inclusion' },
    { name: 'Stick Electrode (E6013 / E7018)',     use: 'Stick welding mild steel',                  note: 'E6013: all-position, light slag, good for thin material; E7018 (low-hydrogen): structural, X-ray quality, must be kept dry (rod oven at 120 °C)' },
    { name: 'Flux-Core Wire (E71T-1)',             use: 'Outdoor/site welding, heavy fabrication',   note: 'Gas-shielded flux-core; good in wind; higher deposition rate than solid wire; slag must be removed between passes' },
    { name: 'Shielding Gas (Ar / Ar+CO₂ / CO₂)',  use: 'MIG, TIG, plasma welding',                  note: 'Pure Ar: TIG and Al MIG; 75Ar/25CO₂ (C25): general steel MIG; 98Ar/2CO₂: SS MIG; pure CO₂: deep penetration/budget; cylinder storage: chained upright' },
    { name: 'Tungsten Electrode (2% Th / 2% Ce / Pure)', use: 'TIG welding',                        note: '2% Cerium (WC20): best universal choice; radioactively inert unlike thoriated; pure tungsten for AC Al welding; grind longitudinally to avoid arc wander' },
  ]},
  {
    cat: 'Workshop Tapes & Sealants', items: [
    { name: 'Masking Tape (Low-Tack / High-Temp)', use: 'Spray paint masking, surface protection',  note: 'Standard: remove within 24 h of application; high-temp (Kapton/brown): rated to 150–200 °C for powder coat masking' },
    { name: 'Gaffer / Duct Tape',                  use: 'Temporary fixturing, cable management',    note: 'Not for high-temp or UV exposure; leaves residue long-term; excellent for shielding during blast/spray operations' },
    { name: 'Aluminium Foil Tape',                 use: 'Heat shielding, HVAC duct sealing, exhaust wrapping assist', note: 'Acrylic adhesive; high-temp rated (up to 120 °C adhesive); aluminium itself rated much higher — adhesive is limiting factor' },
    { name: 'Kapton (Polyimide) Tape',             use: 'PCB masking, powder coat, high-temp electrical', note: 'Rated to 260 °C continuous; dimensionally stable; used in electronics, powder coating, and SMT reflow processes' },
    { name: 'Thread Seal / PTFE Tape',             use: 'Pipe thread sealing (see sealants section)', note: 'See sealants section; PTFE tape only: wrap male threads 2–3 turns clockwise viewed from thread end' },
  ]},
];

// ── Substrate compatibility matrix for adhesives ─────────────────────────────
const COMPAT = [
  { substrate: 'Mild Steel',       ca: 'Excellent', epoxy: 'Excellent', mma: 'Excellent', pu: 'Good',    silicone: 'Moderate', prep: 'Abrade + solvent wipe (MEK/IPA)' },
  { substrate: 'Stainless Steel',  ca: 'Good',      epoxy: 'Excellent', mma: 'Excellent', pu: 'Good',    silicone: 'Moderate', prep: 'Abrade + solvent wipe; primer for CA' },
  { substrate: 'Aluminium',        ca: 'Good',      epoxy: 'Excellent', mma: 'Excellent', pu: 'Good',    silicone: 'Moderate', prep: 'Abrade + IPA wipe; anodised = etch primer' },
  { substrate: 'Copper / Brass',   ca: 'Excellent', epoxy: 'Good',      mma: 'Good',      pu: 'Moderate',silicone: 'Limited',  prep: 'Abrade; acetone wipe; avoid acetic-cure silicone (corrosive)' },
  { substrate: 'HDPE / PP',        ca: 'Poor',      epoxy: 'Poor',      mma: 'Good',      pu: 'Limited', silicone: 'Limited',  prep: 'Flame or corona treat, or Loctite SF 770 primer; surface energy too low otherwise' },
  { substrate: 'ABS / Polycarbonate', ca: 'Excellent', epoxy: 'Good',   mma: 'Excellent', pu: 'Good',    silicone: 'Moderate', prep: 'Solvent wipe only — no abrasion on PC (stress cracking risk); IPA safe' },
  { substrate: 'CFRP / Fibreglass', ca: 'Good',     epoxy: 'Excellent', mma: 'Excellent', pu: 'Good',    silicone: 'Moderate', prep: 'Peel-ply or abrade; solvent wipe; avoid contaminating fibre face' },
  { substrate: 'Natural Rubber',   ca: 'Excellent', epoxy: 'Limited',   mma: 'Good',      pu: 'Excellent',silicone: 'Excellent', prep: 'Buff; solvent wipe; CA 480 (toughened) preferred; primer for epoxy' },
  { substrate: 'Silicone Rubber',  ca: 'Poor',      epoxy: 'Poor',      mma: 'Poor',      pu: 'Poor',    silicone: 'Moderate', prep: 'Special silicone-to-silicone primer (SS4044) required; most adhesives will not bond reliably' },
  { substrate: 'Wood (dry)',        ca: 'Good',      epoxy: 'Excellent', mma: 'Good',      pu: 'Excellent',silicone: 'Limited',  prep: 'Sand 80–120 grit; wipe dust; moisture content <15%; PU excellent for slightly damp wood' },
  { substrate: 'Glass',            ca: 'Good',      epoxy: 'Good',      mma: 'Good',      pu: 'Good',    silicone: 'Excellent', prep: 'IPA wipe; UV-cure adhesive optimal for clear bonds; silicone standard for glazing' },
  { substrate: 'Concrete / Stone', ca: 'Limited',   epoxy: 'Excellent', mma: 'Good',      pu: 'Good',    silicone: 'Good',     prep: 'Grind surface; remove laitance; vacuum; dry; epoxy anchoring compounds for structural fixings' },
];

// ─────────────────────────────────────────────────────────────────────────────
// COMPONENTS
// ─────────────────────────────────────────────────────────────────────────────

const thS = { background: '#1e7a36', color: '#fff', padding: '8px 10px', textAlign: 'left', fontWeight: '700', fontSize: '0.82rem' };
const tdB = { padding: '8px 10px', borderBottom: '1px solid #b8dfc2', verticalAlign: 'top', fontSize: '0.82rem' };
const stripe = i => i % 2 === 0 ? '#e8f5ec' : '#f5faf7';

// ── Threadlocker table ───────────────────────────────────────────────────────
function ThreadlockerTable() {
  const cols = [
    { key: 'grade',       label: 'Grade',          w: '160px' },
    { key: 'strength',    label: 'Strength' },
    { key: 'removable',   label: 'Removable' },
    { key: 'tempMax',     label: 'Max Temp' },
    { key: 'breakTorque', label: 'Break Torque' },
    { key: 'fixture',     label: 'Fixture Time' },
    { key: 'fullCure',    label: 'Full Cure' },
    { key: 'gapFill',     label: 'Gap Fill' },
    { key: 'viscosity',   label: 'Viscosity' },
    { key: 'bestFor',     label: 'Best For',        w: '200px' },
    { key: 'notFor',      label: 'Do NOT Use For',  w: '180px' },
  ];
  const strengthColor = { 'Low': RATING_COLORS['Low'], 'Medium': RATING_COLORS['Medium'], 'High': RATING_COLORS['High'], 'High / Permanent': RATING_COLORS['High'] };
  return (
    <div style={{ overflowX: 'auto' }}>
      <table style={{ width: '100%', borderCollapse: 'collapse', marginBottom: '20px' }}>
        <thead><tr>{cols.map(c => <th key={c.key} style={{ ...thS, width: c.w || 'auto' }}>{c.label}</th>)}</tr></thead>
        <tbody>
          {THREADLOCKERS.map((r, i) => (
            <tr key={i}>
              {cols.map(c => {
                const bg = stripe(i);
                if (c.key === 'grade') return <td key={c.key} style={{ ...tdB, background: bg, fontWeight: '700', color: '#145a24', fontFamily: "'Georgia', serif", whiteSpace: 'nowrap' }}>{r[c.key]}</td>;
                if (c.key === 'strength') {
                  const sc = strengthColor[r.strength] || { bg: '#e9ecef', text: '#444', border: '#bbb' };
                  return <td key={c.key} style={{ ...tdB, background: bg }}><span style={{ background: sc.bg, color: sc.text, border: `1px solid ${sc.border}`, borderRadius: '4px', padding: '2px 7px', fontSize: '0.8rem', fontWeight: '600', display: 'inline-block' }}>{r[c.key]}</span></td>;
                }
                if (c.key === 'removable') return <td key={c.key} style={{ ...tdB, background: bg }}><RatingBadge value={r[c.key]} /></td>;
                if (c.key === 'bestFor') return <td key={c.key} style={{ ...tdB, background: bg, color: '#1a3a1a' }}>{r[c.key]}</td>;
                if (c.key === 'notFor') return <td key={c.key} style={{ ...tdB, background: bg, color: '#5a1a1a' }}>{r[c.key]}</td>;
                return <td key={c.key} style={{ ...tdB, background: bg, fontFamily: "'Courier New', monospace", fontSize: '0.8rem' }}>{r[c.key]}</td>;
              })}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// ── Retaining compounds table ────────────────────────────────────────────────
function RetainingTable() {
  const cols = [
    { key: 'grade',      label: 'Grade',        w: '160px' },
    { key: 'strength',   label: 'Strength' },
    { key: 'shearStr',   label: 'Shear Strength' },
    { key: 'tempMax',    label: 'Max Temp' },
    { key: 'fixture',    label: 'Fixture Time' },
    { key: 'fullCure',   label: 'Full Cure' },
    { key: 'gapFill',    label: 'Gap Fill' },
    { key: 'bestFor',    label: 'Best For',     w: '200px' },
    { key: 'notFor',     label: 'Do NOT Use For', w: '170px' },
  ];
  return (
    <div style={{ overflowX: 'auto' }}>
      <table style={{ width: '100%', borderCollapse: 'collapse', marginBottom: '20px' }}>
        <thead><tr>{cols.map(c => <th key={c.key} style={{ ...thS, width: c.w || 'auto' }}>{c.label}</th>)}</tr></thead>
        <tbody>
          {RETAINING.map((r, i) => (
            <tr key={i}>
              {cols.map(c => {
                const bg = stripe(i);
                if (c.key === 'grade') return <td key={c.key} style={{ ...tdB, background: bg, fontWeight: '700', color: '#145a24', fontFamily: "'Georgia', serif", whiteSpace: 'nowrap' }}>{r[c.key]}</td>;
                if (c.key === 'bestFor') return <td key={c.key} style={{ ...tdB, background: bg, color: '#1a3a1a' }}>{r[c.key]}</td>;
                if (c.key === 'notFor') return <td key={c.key} style={{ ...tdB, background: bg, color: '#5a1a1a' }}>{r[c.key]}</td>;
                return <td key={c.key} style={{ ...tdB, background: bg, fontFamily: "'Courier New', monospace", fontSize: '0.8rem' }}>{r[c.key]}</td>;
              })}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// ── Sealants table ────────────────────────────────────────────────────────────
function SealantsTable() {
  const cols = [
    { key: 'grade',    label: 'Grade',      w: '180px' },
    { key: 'type',     label: 'Type' },
    { key: 'temp',     label: 'Temp Range' },
    { key: 'pressure', label: 'Pressure Rating' },
    { key: 'fixture',  label: 'Fixture Time' },
    { key: 'fullCure', label: 'Full Cure' },
    { key: 'bestFor',  label: 'Best For',   w: '200px' },
    { key: 'note',     label: 'Notes',      w: '200px' },
  ];
  return (
    <div style={{ overflowX: 'auto' }}>
      <table style={{ width: '100%', borderCollapse: 'collapse', marginBottom: '20px' }}>
        <thead><tr>{cols.map(c => <th key={c.key} style={{ ...thS, width: c.w || 'auto' }}>{c.label}</th>)}</tr></thead>
        <tbody>
          {SEALANTS.map((r, i) => (
            <tr key={i}>
              {cols.map(c => {
                const bg = stripe(i);
                if (c.key === 'grade') return <td key={c.key} style={{ ...tdB, background: bg, fontWeight: '700', color: '#145a24', fontFamily: "'Georgia', serif" }}>{r[c.key]}</td>;
                if (c.key === 'bestFor') return <td key={c.key} style={{ ...tdB, background: bg, color: '#1a3a1a' }}>{r[c.key]}</td>;
                if (c.key === 'note') return <td key={c.key} style={{ ...tdB, background: bg, fontStyle: 'italic', fontSize: '0.8rem' }}>{r[c.key]}</td>;
                return <td key={c.key} style={{ ...tdB, background: bg, fontFamily: "'Courier New', monospace", fontSize: '0.8rem' }}>{r[c.key]}</td>;
              })}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// ── Adhesives table ───────────────────────────────────────────────────────────
function AdhesivesTable() {
  const cols = [
    { key: 'name',         label: 'Adhesive',         w: '200px' },
    { key: 'category',     label: 'Chemistry' },
    { key: 'fixture',      label: 'Fixture' },
    { key: 'fullCure',     label: 'Full Cure' },
    { key: 'gapFill',      label: 'Gap Fill' },
    { key: 'shearStr',     label: 'Shear Str.' },
    { key: 'tempRange',    label: 'Temp Range' },
    { key: 'metalBond',    label: 'Metal' },
    { key: 'plasticBond',  label: 'Plastic' },
    { key: 'rubberBond',   label: 'Rubber' },
    { key: 'woodBond',     label: 'Wood' },
    { key: 'waterResist',  label: 'Water Res.' },
    { key: 'heatResist',   label: 'Heat Res.' },
    { key: 'bestFor',      label: 'Best For',          w: '180px' },
    { key: 'notFor',       label: 'Avoid For',         w: '160px' },
  ];
  const ratingKeys = ['metalBond','plasticBond','rubberBond','woodBond','waterResist','heatResist'];
  return (
    <div style={{ overflowX: 'auto' }}>
      <table style={{ width: '100%', borderCollapse: 'collapse', marginBottom: '20px' }}>
        <thead><tr>{cols.map(c => <th key={c.key} style={{ ...thS, width: c.w || 'auto' }}>{c.label}</th>)}</tr></thead>
        <tbody>
          {ADHESIVES.map((r, i) => (
            <tr key={i}>
              {cols.map(c => {
                const bg = stripe(i);
                if (c.key === 'name') return <td key={c.key} style={{ ...tdB, background: bg, fontWeight: '700', color: '#145a24', fontFamily: "'Georgia', serif" }}>{r[c.key]}</td>;
                if (ratingKeys.includes(c.key)) return <td key={c.key} style={{ ...tdB, background: bg }}><RatingBadge value={r[c.key]} /></td>;
                if (c.key === 'bestFor') return <td key={c.key} style={{ ...tdB, background: bg, color: '#1a3a1a', fontSize: '0.8rem' }}>{r[c.key]}</td>;
                if (c.key === 'notFor') return <td key={c.key} style={{ ...tdB, background: bg, color: '#5a1a1a', fontSize: '0.8rem' }}>{r[c.key]}</td>;
                return <td key={c.key} style={{ ...tdB, background: bg, fontFamily: "'Courier New', monospace", fontSize: '0.79rem' }}>{r[c.key]}</td>;
              })}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// ── Substrate compatibility table ─────────────────────────────────────────────
function CompatTable() {
  const cols = [
    { key: 'substrate', label: 'Substrate',         w: '180px' },
    { key: 'ca',        label: 'CA (Super Glue)' },
    { key: 'epoxy',     label: 'Epoxy (2K)' },
    { key: 'mma',       label: 'MMA/Acrylic (2K)' },
    { key: 'pu',        label: 'Polyurethane' },
    { key: 'silicone',  label: 'Silicone RTV' },
    { key: 'prep',      label: 'Surface Prep Guidance',  w: '220px' },
  ];
  const ratingKeys = ['ca','epoxy','mma','pu','silicone'];
  return (
    <div style={{ overflowX: 'auto' }}>
      <table style={{ width: '100%', borderCollapse: 'collapse', marginBottom: '20px' }}>
        <thead><tr>{cols.map(c => <th key={c.key} style={{ ...thS, width: c.w || 'auto' }}>{c.label}</th>)}</tr></thead>
        <tbody>
          {COMPAT.map((r, i) => (
            <tr key={i}>
              {cols.map(c => {
                const bg = stripe(i);
                if (c.key === 'substrate') return <td key={c.key} style={{ ...tdB, background: bg, fontWeight: '700', color: '#145a24', fontFamily: "'Georgia', serif" }}>{r[c.key]}</td>;
                if (ratingKeys.includes(c.key)) return <td key={c.key} style={{ ...tdB, background: bg }}><RatingBadge value={r[c.key]} /></td>;
                return <td key={c.key} style={{ ...tdB, background: bg, fontSize: '0.8rem', fontStyle: 'italic' }}>{r[c.key]}</td>;
              })}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// ── General consumables table ─────────────────────────────────────────────────
function ConsumablesTable() {
  const catColors = {
    'Cutting Fluids':                    '#c6ecd9',
    'Thread-Forming & Assembly Compounds':'#fde2b8',
    'Lubricants':                        '#c6efce',
    'Abrasives':                         '#fde8c0',
    'Welding Consumables':               '#f8d7da',
    'Workshop Tapes & Sealants':         '#e8d5f5',
  };
  return (
    <div style={{ overflowX: 'auto' }}>
      <table style={{ width: '100%', borderCollapse: 'collapse', marginBottom: '20px' }}>
        <thead>
          <tr>
            <th style={{ ...thS, width: '160px' }}>Category</th>
            <th style={{ ...thS, width: '220px' }}>Item</th>
            <th style={thS}>Primary Use</th>
            <th style={{ ...thS, width: '280px' }}>Notes / Critical Details</th>
          </tr>
        </thead>
        <tbody>
          {CONSUMABLES.flatMap((cat, ci) =>
            cat.items.map((item, ii) => {
              const rowIdx = CONSUMABLES.slice(0, ci).reduce((s, c) => s + c.items.length, 0) + ii;
              const bg = stripe(rowIdx);
              return (
                <tr key={`${ci}-${ii}`}>
                  {ii === 0
                    ? <td rowSpan={cat.items.length} style={{ ...tdB, background: catColors[cat.cat] || '#e8f5ec', fontWeight: '700', color: '#145a24', fontFamily: "'Georgia', serif", verticalAlign: 'middle', fontSize: '0.85rem', textAlign: 'center', writingMode: 'vertical-rl', transform: 'rotate(180deg)', width: '36px', padding: '12px 8px' }}>{cat.cat}</td>
                    : null}
                  <td style={{ ...tdB, background: bg, fontWeight: '600', color: '#1a3a1a', fontFamily: "'Georgia', serif" }}>{item.name}</td>
                  <td style={{ ...tdB, background: bg, color: '#0a1f0e' }}>{item.use}</td>
                  <td style={{ ...tdB, background: bg, fontStyle: 'italic', fontSize: '0.8rem', color: '#2a3a2a' }}>{item.note}</td>
                </tr>
              );
            })
          )}
        </tbody>
      </table>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// MAIN EXPORT
// ─────────────────────────────────────────────────────────────────────────────
export default function WorkshopConsumables() {
  return (
    <div style={S.wrapper}>
      <h1 style={S.h1}>Workshop Consumables, Loctite & Adhesives Reference</h1>
      <div style={S.subtitle}>
        Comprehensive selection, application, and specification reference for threadlockers, retaining compounds,
        pipe sealants, structural and general-purpose adhesives, cutting fluids, lubricants, abrasives, and welding
        consumables. Values represent typical data at 23 °C on steel unless stated; always verify against
        manufacturer technical data sheets and SDS for the specific product and substrate combination.
      </div>

      {/* Legend */}
      <div style={S.legendBox}>
        <strong style={{ fontSize: '0.85rem', color: '#145a24', alignSelf: 'center' }}>Rating Key:</strong>
        {[['Excellent', RATING_COLORS['Excellent']], ['Good', RATING_COLORS['Good']], ['Moderate', RATING_COLORS['Moderate']],
          ['Limited', RATING_COLORS['Limited']], ['Poor', RATING_COLORS['Poor']],
          ['Yes', RATING_COLORS['Yes']], ['Partial', RATING_COLORS['Partial']], ['No', RATING_COLORS['No']],
          ['High', RATING_COLORS['High']], ['Medium', RATING_COLORS['Medium']], ['Low', RATING_COLORS['Low']],
          ['Permanent', RATING_COLORS['Permanent']], ['Removable', RATING_COLORS['Removable']],
        ].map(([label, c]) => (
          <div key={label} style={S.legendItem}>
            <div style={{ ...S.legendDot, background: c.bg, border: `1px solid ${c.border}` }} />
            <span style={{ color: c.text, fontWeight: '600' }}>{label}</span>
          </div>
        ))}
      </div>

      {/* ── SECTION 1 — Anaerobic Basics ── */}
      <h2 style={S.h2}>Anaerobic Adhesives — How They Work</h2>
      <p style={S.p}>
        Threadlockers, retaining compounds, and pipe sealants from Loctite (Henkel) and equivalent brands are all
        <em> anaerobic adhesives</em> — they cure in the absence of oxygen when in contact with metal ions.
        Squeeze out from the joint exposed to air stays liquid indefinitely; the trapped portion between metal
        surfaces cures to a thermoset plastic within minutes to hours depending on metal, temperature, and activator use.
      </p>
      <div style={S.noteBox}>
        ℹ️ <strong>Passive Metals Need Primer:</strong> Stainless steel, zinc/cadmium plating, aluminium, and titanium
        have passive oxide layers that inhibit anaerobic cure. Always use Loctite SF 7649 (green) or SF 7471 (primer N)
        activator/primer on these substrates or cure will be very slow and incomplete. Apply primer, allow 30–60 s to
        dry, then apply adhesive as normal.
      </div>
      <Table2 col1Label="Concept" col2Label="Explanation" rows={[
        ['Cure Mechanism',       'Anaerobic = cures without oxygen. Metal ions (Fe²⁺, Cu²⁺) act as catalysts initiating free-radical polymerisation.\nOxygen in air acts as inhibitor — keeps adhesive liquid in bottle. Remove oxygen (close fit) + metal contact = cure begins.'],
        ['Gap Fill Limit',       'Most threadlockers: 0.08–0.15 mm. Retaining compounds: 0.10–0.25 mm. Pipe sealants: fill thread form.\nBeyond gap limit, oxygen cannot be fully excluded and cure is incomplete in the gap. Use a filler or structural adhesive for larger gaps.'],
        ['Temperature Effect',   'Cure time halves for every ~10 °C increase above 23 °C. Below 10 °C cure slows dramatically — use accelerator.\nFull cure specification is at 23 °C; check datasheet for cure curves at your ambient temperature.'],
        ['Activator / Primer',   'SF 7649 (green spray): general activator for passive metals and slow-curing conditions.\nSF 7471 (clear liquid): activator applied to one surface; adhesive to the other — doubles speed on all metals.\nSF 770 (pink): required for polyolefin plastics before CA adhesives (not anaerobic).'],
        ['Disassembly of Reds',  'Apply heat: warm fastener to 230 °C with heat gun. Apply break torque while fastener is still hot.\nCooling re-cures — work quickly. Do not use open flame on zinc-plated fasteners (toxic fumes).'],
        ['Shelf Life & Storage', 'Unopened: 24 months at 8–21 °C in original bottle. Keep refrigerated (not frozen) for extended life.\nKeep away from metal tools/containers — trace metals on dropper tip can cause in-bottle gellation.\nSqueeze bottle to expose only what is needed; do not pour back.'],
        ['Torque Reduction',     'Anti-seize and threadlockers both affect torque values. For anaerobic: use same torque as dry threads.\nNOTE: This differs from lubricants/anti-seize where specified torque must be reduced by ~20% (K factor adjustment).'],
      ]} />

      {/* ── SECTION 2 — Threadlocker selection ── */}
      <h2 style={S.h2}>Threadlocker Selection & Specification Matrix</h2>
      <p style={S.p}>
        Loctite colour coding is a reliable guide but not the only differentiator — viscosity, temperature rating,
        and wicking versus pre-assembly application are equally important. The table covers the most common grades
        in workshops; equivalent products from Henkel (Loctite), Permatex, Vibra-Tite, and Permabond follow
        the same colour and strength conventions in most cases.
      </p>
      <div style={S.noteBox}>
        ⚠️ <strong>Torque Reference:</strong> Break-torque values are on cadmium-plated steel M10 fasteners at 24 h.
        Values on stainless without primer will be 30–50% lower. Values increase at higher temperatures of cure.
        Always use the Loctite torque reference chart for your specific fastener size and material.
      </div>
      <ThreadlockerTable />
      <InfoBox title="Threadlocker Grade Quick-Select" items={[
        'Small screws / instruments (M2–M6), need to re-adjust later → 222 Purple',
        'General workshop fasteners (M6–M20), standard vibration resistance → 243 Blue (most common)',
        'Already assembled bolt — apply after tightening → 270 or 290 Green (wicking grade)',
        'High-vibration machinery, permanent (M10–M25), need heat to remove → 271 Red',
        'Exhaust manifold, engine mount, continuous >150 °C service → 272 Red high-temp',
        'Stainless, zinc-plated, or aluminium threads — any grade → Apply SF 7649 primer first',
        'Below 10 °C ambient cure → Apply SF 7471 activator to one thread surface',
      ]} />

      {/* ── SECTION 3 — Retaining compounds ── */}
      <h2 style={S.h2}>Retaining Compounds (Cylindrical Assembly)</h2>
      <p style={S.p}>
        Retaining compounds fill the micro-clearance between bore and shaft, transforming a slip or
        transition fit into a fully locked assembly without the cost of machining an interference fit.
        They are also widely used for <em>repair</em> — when a bore has worn slightly beyond its
        original tolerance, retaining compound can restore full load-carrying capability.
      </p>
      <div style={S.dangerBox}>
        ⛔ <strong>Assembly method critical:</strong> Apply retaining compound to both mating surfaces, slide
        (do not press) the parts together, and align immediately. Do NOT apply and then force-press with a hydraulic press —
        the compound will be scraped off. Use 638 on slip-fit parts if pressing is needed immediately.
      </div>
      <RetainingTable />

      {/* ── SECTION 4 — Pipe & Flange sealants ── */}
      <h2 style={S.h2}>Pipe Sealants & Formed-In-Place Gaskets</h2>
      <p style={S.p}>
        Anaerobic pipe sealants outperform PTFE tape on metal-to-metal fittings: they seal the full
        thread form, resist vibration loosening, resist most common fluids, and allow more consistent
        make-up torque. RTV silicone and anaerobic flange sealants replace cut gaskets for machined
        metal flanges.
      </p>
      <SealantsTable />
      <div style={S.noteBox}>
        ⚠️ <strong>Pressure Ratings:</strong> Given values are on steel hydraulic fittings. Ratings on aluminium,
        brass, or plastic fittings are lower — consult Loctite fitting sealant datasheet. RTV gaskets: no pressure
        rating applies — must be used with flanges that have adequate clamping load; do not use on flanges with
        uneven or warped surfaces without surfacing first.
      </div>

      {/* ── SECTION 5 — Structural adhesives ── */}
      <h2 style={S.h2}>Structural & General-Purpose Adhesive Reference</h2>
      <p style={S.p}>
        Structural adhesives bond substrates by load transfer across the joint area — not by mechanical
        interlocking. For this reason, <strong>surface preparation is the single most important variable</strong>;
        even the best adhesive fails on a contaminated or low-energy surface. The table below covers the
        main chemistry types used in workshops.
      </p>
      <div style={S.dangerBox}>
        ⛔ <strong>Surface prep is not optional:</strong> Oil, release agent, zinc chromate primer, or mill scale
        on the bond face will cause adhesive failure at a fraction of rated strength. Abrade → solvent wipe (IPA or MEK)
        → bond within 15 minutes for metals. Even fingerprints after wiping can reduce bond strength by &gt;50%.
      </div>
      <AdhesivesTable />

      {/* ── SECTION 6 — Substrate compatibility ── */}
      <h2 style={S.h2}>Adhesive-to-Substrate Compatibility Matrix</h2>
      <p style={S.p}>
        Compatibility ratings assume correct surface preparation for each substrate. "Poor" does not mean
        zero adhesion — it means the joint cannot reliably carry structural load and will likely fail over
        time or under peel/impact loading. Always test on your actual substrates before committing to
        production use.
      </p>
      <CompatTable />
      <InfoBox title="Surface Preparation by Substrate — Critical Rules" items={[
        'Steel (mild): Abrade 80–120 grit → solvent wipe (MEK or acetone) → bond within 15 min. Remove all rust, scale, oil.',
        'Aluminium: Abrade (not steel wool — causes galvanic) → IPA wipe → bond. Anodised surface: etch or use etch primer for structural bonds.',
        'Stainless steel: Abrade with SS-only abrasive (prevent contamination) → IPA wipe → use primer for anaerobic adhesives.',
        'HDPE / PP / Polyolefins: Flame-treat or corona discharge, OR apply Loctite SF 770 primer. These surfaces have very low surface energy (~30 mN/m) — most adhesives will not wet and bond without surface activation.',
        'PC (Polycarbonate): IPA wipe only — no abrasion (causes stress cracking). Avoid aggressive solvents (MEK, acetone, toluene) — they dissolve PC.',
        'CFRP / Fibreglass: Peel-ply (best), or 120 grit abrade → compressed air blow → IPA wipe. Avoid sanding through outer layer. Solvent must fully evaporate before bonding.',
        'Rubber: Buff lightly, apply CA primer (Loctite SF 770) or use 480/toughened CA for rubber-to-metal. PU adhesive for flexible joints.',
        'Silicone rubber: Special case — use Loctite SF SS4044 silicone bonding primer. No standard adhesive will bond reliably to cured silicone without it.',
        'Wood: Sand 80–120 grit, remove dust, ensure moisture content <15%. PU adhesive tolerates slightly damp wood (moisture activates cure). Epoxy is best for gap-filling structural wood joints.',
      ]} />

      {/* ── SECTION 7 — General consumables ── */}
      <h2 style={S.h2}>General Workshop Consumables Reference</h2>
      <p style={S.p}>
        The table below covers cutting fluids, lubricants, abrasives, welding consumables, and workshop tapes —
        items consumed during production and maintenance that have significant impact on tool life, surface quality,
        weld integrity, and equipment longevity. Critical application notes are included where incorrect selection
        commonly causes avoidable failure.
      </p>
      <div style={S.noteBox}>
        ⚠️ <strong>Coolant maintenance:</strong> Water-miscible cutting coolant concentration must be checked weekly
        with a refractometer. Low concentration = poor lubrication + bacterial growth (biocide needed). High concentration
        = skin irritation + residue buildup. Target Brix typically gives 5–10% by volume depending on grade — check
        TDS for your specific coolant product.
      </div>
      <ConsumablesTable />

      {/* ── SECTION 8 — Adhesive cure & environment ── */}
      <h2 style={S.h2}>Adhesive Curing Conditions, Environment & Failure Modes</h2>
      <Table2 col1Label="Parameter" col2Label="Detail" rows={[
        ['Temperature & Cure Rate',     'Most adhesives specified at 23 °C. Below 10 °C: CA slows significantly, anaerobic may not cure without primer, epoxy is impractically slow.\nHeat acceleration: most 2K epoxies: 60 °C/30 min or 80 °C/1 h achieves comparable strength to 24 h at 23 °C. Do not exceed manufacturer max cure temp.'],
        ['Humidity',                    'CA (cyanoacrylate): requires moisture to cure — dry surfaces and very low humidity (<20% RH) slow cure. High humidity (>80%) accelerates cure but can cause frosting/blooming on transparent surfaces.\nEpoxy: insensitive to humidity during cure but contaminated (wet) surfaces dramatically reduce adhesion.'],
        ['Joint Geometry & Stress Mode','Adhesives are strongest in shear; weakest in peel and cleavage. Design for shear loading wherever possible.\nCA: very brittle — poor in impact and peel; add fillet bead of flexible adhesive at joint edges for durability.\nEpoxy (rigid): good in shear/compression, brittle in impact/peel. Toughened epoxy reduces this.\nPU / MMA: better peel and impact resistance — preferred for structural applications with dynamic loading.'],
        ['Contamination Failure',       'The most common adhesive failure mode. Contamination sources: fingerprints (oils), release agents, machining coolant, oxide layers, silicone contamination (catastrophic — one silicone-contaminated surface can ruin an entire batch).\nIdentify silicone contamination: water break test — water beads on contaminated surface, sheets off clean metal.'],
        ['Adhesive Selection: Pot Life', 'Pot life (working time) = time after mixing until viscosity doubles/becomes unworkable.\n2K epoxy: typically 20–90 min at 23 °C depending on grade. Heat accelerates: 40 °C halves pot life.\nCA: no pot life concern — single component. MMA: 3–15 min fast grades; 30–90 min slow grades.\nMix only what you can use in the first half of the pot life to ensure wet application to surface.'],
        ['Primer Systems',              'Loctite SF 770: polyolefin primer — enables CA bonding to PP/PE/PTFE. Apply to substrate, 60 s dry, then apply CA.\nLoctite SF 7649: green anaerobic activator — passive metals, low temperature, accelerated cure.\nLoctite SF 7471: clear anaerobic primer N — all metals including passive; apply to one surface.\nLoctite SF 7455: CA accelerator spray — instantly cures CA on command (e.g., for fixturing large assemblies).'],
        ['Storage',                     'CA: 8–21 °C, refrigerate to extend shelf life; keep nozzle clean; do NOT freeze.\nEpoxy components (separate A/B): 2 years shelf at room temp; do not freeze hardener.\nAnaerobic (Loctite bottles): 8–21 °C; do not fill bottles with metal tools; keep caps on; refrigerate if possible.\nRTV Silicone: 12–24 months; keep tube sealed; discard if skin has formed in nozzle beyond 10 mm.'],
        ['Blooming / Frosting (CA)',    'White residue around CA joint = monomer vapour depositing on cold/humid surface. Caused by: too much adhesive applied, cold substrate, high humidity.\nFix: use minimum adhesive quantity (capillary fill only); warm substrate; ventilate. Use Loctite SF 7452 CA accelerator to cure rapidly before bloom develops.'],
      ]} />

      {/* ── SECTION 9 — Welding consumable selection guide ── */}
      <h2 style={S.h2}>Welding Consumable Selection Guide</h2>
      <InfoBox title="MIG Wire Selection by Base Metal" items={[
        'Mild steel (A36, 1018, etc.) → ER70S-6 (most common); ER70S-3 for clean plate without mill scale',
        'Structural steel (A572 Grade 50) → ER70S-6 or ER80S-D2 for higher strength match',
        '304 Stainless → ER308L wire; 316 Stainless → ER316L wire; do not use mild steel wire on SS',
        'Aluminium 6061/6063 → ER4043 (good flow, less crack-sensitive); structural Al joints → ER5356 (higher strength)',
        'Aluminium 5052/5083 → ER5356; do not use ER4043 on 5xxx series (Si causes hot cracking)',
        'Cast iron (repair) → Nickel-based wire (ENiFe-CI) or preheat + E7018 stick; slow cool under insulating blanket',
        'Corten (weathering steel) → ER70S-6 acceptable; specialist Corten wire for colour-matched welds',
      ]} />
      <InfoBox title="Shielding Gas Selection" items={[
        'Mild steel MIG: 75%Ar / 25%CO₂ (C25) — best balance of arc stability, penetration, and spatter',
        'Mild steel MIG (budget/outdoor): 100% CO₂ — deeper penetration, more spatter, lower cost; wind-tolerant',
        'Stainless MIG: 98%Ar / 2%CO₂ — low CO₂ to prevent carbon pickup (sensitisation) in SS',
        'Stainless / exotic alloy MIG: Tri-mix (90He/7.5Ar/2.5CO₂) — premium; superior weld profile and heat input control',
        'Aluminium MIG: 100% Argon — no CO₂ or O₂ on Al (causes porosity and black deposits)',
        'TIG (all metals): 100% Argon; high-purity grade (99.997%+); helium addition increases heat input for thick Al',
        'Flux-core (FCAW-G): 75/25 C25 or 100% CO₂; check wire spec — some flux-core wires are self-shielded (no gas)',
      ]} />
      <InfoBox title="Stick Electrode (SMAW) Selection" items={[
        'E6013 — All-position, soft arc, easy slag removal, good for thin/dirty/rusty material; general repair welding',
        'E6011 — All-position including vertical-up; AC or DC; penetrates through rust and mill scale; pipe root passes',
        'E7018 (Low Hydrogen) — Premium structural: X-ray quality, low hydrogen to prevent cracking in higher-carbon steels',
        'E7018 storage is critical: must be kept in rod oven at 120–150 °C; once out, use within 4 h or re-dry at 300 °C/1 h',
        'E308L-16 / E316L-16 — Stainless steel repair; low-carbon (L) grade prevents sensitisation; use on clean SS only',
        'ENi-CI / ENiFe-CI — Cast iron repair; high-nickel; minimal preheat needed vs standard electrodes; peening recommended',
      ]} />

      {/* ── SECTION 10 — Quick-reference selection guides ── */}
      <h2 style={S.h2}>Product Selection Decision Trees</h2>
      <InfoBox title="Threadlocker: Need to Remove Later?" items={[
        'YES, by hand (screwdriver / hex key) → 222 Purple (M2–M8) or 243 Blue (M6–M20)',
        'YES, but only with spanner/impact gun → 263 Black or 271 Red (heat to 230 °C)',
        'NO, permanent retention required → 271 Red (standard temp) or 272 Red (>150 °C service)',
        'Assembly already done, want to wick in → 270 Green (medium strength) or 290 Green (high strength)',
        'Stainless, zinc, aluminium threads → ANY grade + SF 7649 primer first',
      ]} />
      <InfoBox title="Adhesive: What Are You Bonding?" items={[
        'Metal-to-metal, load-bearing, gap <0.15 mm, fast bond → CA (401/406) or MMA (3430)',
        'Metal-to-metal, structural, larger gap, high strength → 2K Epoxy (EA9394 / Araldite Standard)',
        'Metal-to-metal with vibration/peel/impact loading → MMA (methacrylate) — better peel than epoxy',
        'Metal/plastic/rubber, flexible joint, sealing too → 1K Polyurethane (Sikaflex-252)',
        'High-temp sealing or gasket replacement → Silicone RTV (Loctite 5900 / Dow 732)',
        'Rubber-to-metal, instant bond → Loctite 480 (toughened CA, black)',
        'Polyolefin plastic (PP/PE/HDPE) → Apply SF 770 primer first, then CA — or use MMA',
        'Silicone rubber → Use SS4044 silicone primer; no other common adhesive will bond reliably',
        'Concrete / structural anchor → 2K Epoxy injection compound (Hilti HIT-RE 500 / Sika AnchorFix)',
      ]} />
      <InfoBox title="Cutting Fluid: What Are You Machining?" items={[
        'General CNC turning/milling (steel, cast iron) → Water-miscible emulsion 5–10%; check concentration weekly',
        'Stainless or titanium (tapping, threading) → Tapping paste (sulphurised) or neat cutting oil; DO NOT use water-miscible on stainless hand tapping without paste',
        'Aluminium CNC → Aluminium-specific water-miscible or kerosene-based fluid; prevent BUE',
        'Aluminium hand drilling → Isopropanol or WD-40 acceptable for occasional use',
        'Brass / copper (turning) → Dry or light mineral oil; no sulphurised fluid (stains copper)',
        'Cast iron (milling/drilling) → Dry preferred; water-miscible if needed; no neat oil (mist + chip = fire risk)',
        'Hardened steel (grinding) → Water-soluble grinding oil; concentration typically higher 7–12%; temperature control critical',
      ]} />

      {/* ── SECTION 11 — Safety & storage ── */}
      <h2 style={S.h2}>Safety, Storage & Regulatory Notes</h2>
      <div style={S.dangerBox}>
        ⛔ <strong>Cyanoacrylate Skin/Eye Bonding:</strong> CA bonds skin in 2–5 seconds. Do not rub bonded skin —
        peel gently with rolling motion. Soak in warm soapy water 5–10 min to soften. Use acetone (nail polish remover)
        to dissolve bond. CA vapour causes eye and throat irritation — ventilate. Never use CA near the face without
        keeping eyes closed or protected.
      </div>
      <div style={S.dangerBox}>
        ⛔ <strong>Isocyanates (2K PU, some 2K coatings):</strong> Sensitisers — once sensitised, any exposure causes
        severe asthma reaction. P100 + OV respirator minimum; supplied air preferred in confined spaces. Isocyanate
        exposure is permanent sensitisation — no "safe" exposure after sensitisation occurs.
      </div>
      <Table2 col1Label="Hazard Topic" col2Label="Guidance" rows={[
        ['CA (Cyanoacrylate) Safety',   'Skin bond: warm soapy water + mechanical separation; acetone dissolves after 5–10 min soak.\nVapour: ventilate; irritant to throat and eyes; use in well-ventilated areas. Eye splash: flush immediately with water for 15 min — seek medical attention even if no pain.\nFlammable: flash point ~85 °C (liquid); disposed rags can be a fire risk — place in water before disposal.'],
        ['Epoxy Safety',               '2K epoxy components (especially hardener/amine) are skin sensitisers on repeated contact.\nAlways use nitrile gloves; do not use bare hands for mixing. Sensitisation can develop after months of exposure with no reaction — once sensitised, any contact triggers reaction.\nMixed epoxy generates exothermic heat — large masses (>200 g) in a pot can reach 150 °C+ and cause burns or fire. Mix in wide flat containers, not deep cups.'],
        ['Anaerobic Sealant Safety',   'Generally low hazard; mild skin/eye irritant; vapour at low levels.\nAvoid eye contact; nitrile gloves recommended for repeated use. Activator sprays (SF 7649) are flammable aerosols — keep from ignition sources. Dispose of used cloths with activator residue as flammable waste.'],
        ['Cutting Fluid Safety',       'Water-miscible coolants can grow bacteria and mould (especially if concentration drops). Biocide addition needed periodically; do not use with open cuts on hands.\nNeat mineral cutting oil: fire risk near welding/cutting sparks — segregate areas. Mist from high-speed machining: respiratory concern — LEV (local exhaust ventilation) required.\nAlways check SDS for specific coolant; some contain biocides that are skin sensitisers.'],
        ['Welding Fume',               'All welding fume is classified as a Group 1 carcinogen (IARC). Stainless welding produces Cr(VI) — especially hazardous (lung cancer risk).\nP100 half-face respirator minimum for any production welding; PAPR recommended for continuous welding shifts. LEV (fume extraction) at source is required under most jurisdictions for enclosed welding. Never weld galvanised steel without supplied-air — zinc fume causes metal fume fever.'],
        ['Compressed Gas Cylinders',   'Store upright, chained against wall; store O₂ separately from fuel gas (min 3 m or fire-rated barrier).\nUse correct regulator for gas type — never swap regulators between dissimilar gases. Never use oil/grease on O₂ fittings (explosion risk). Roll cylinders on base edge — never drag or drop. Transport secured; valve cap on when not in use.'],
        ['Storage Compatibility',      'Do NOT store together: flammable solvents + oxidisers; CA accelerators + CA adhesives (violent reaction if mixed); acids + bases.\nSeparate flammable liquids >10 L into dedicated flammable cabinet. Temperature-sensitive adhesives (CA, anaerobic) away from heat sources and sunlight.'],
        ['SDS / Regulatory',           'SDS (Safety Data Sheet) required for all hazardous products under GHS/OSHA HazCom 2012 and EU CLP (1272/2008).\nAll staff using chemical products must be trained and SDS must be accessible at point of use. SDS must be current version — manufacturers update on reformulation.'],
      ]} />

      <InfoBox title="Key Caveats & Limitations" items={[
        'All adhesive strength values are on cadmium-plated (or as noted) steel lap-shear specimens cured at 23 °C and tested per ISO 4587 / ASTM D1002. Actual joint strength depends on substrate material, surface preparation, joint geometry, loading mode, and temperature.',
        'Threadlocker torque values are typical ranges for the stated fastener diameter on steel/cadmium. Values on stainless without primer are 30–50% lower. Always consult the Loctite Torque Reference Guide for your specific application.',
        'Retaining compound shear strength values are on ground steel pins in steel bores at 25 µm diametral clearance. Larger clearances significantly reduce strength.',
        'Cure times are at 23 °C. Cold workshops (below 10 °C) will extend cure times significantly — use primer/activator and allow additional cure time before loading.',
        'Chemical compatibility (coolant-to-seal, solvent-to-substrate) must always be verified with the SDS and the manufacturer\'s chemical resistance guide for the specific formulation in use — generic chemical families vary widely.',
        'Sources: Loctite/Henkel product TDS library (2025); Sika product data sheets; Dow Corning 732 TDS; 3M DP460 product data; Araldite Standard/Rapid TDS; Lord Fusor 120 TDS; Lincoln Electric filler metal selector; Miller Welding gas reference; OSHA 29 CFR 1926.350 (compressed gas); IARC Monographs Vol 118 (welding fume); Henkel Loctite Torque Reference Guide 2024.',
      ]} />
    </div>
  );
}